import { useState, useEffect, useRef } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader } from './Stepper.jsx';
import AvatarDropzone from './AvatarDropzone.jsx';
import { PASSWORD_RULES, PasswordRules } from './PasswordRules.jsx';
import CountryPhoneInput from './CountryPhoneInput.jsx';
import { maskCep } from '../utils/format.js';
import { DEFAULT_COUNTRY, getCountry } from '../utils/countries.js';
import { updateMe, uploadAvatar, changePassword, lookupCep } from '../api/patients.js';

// Endereço salvo hoje como string única ("Rua Example, 45") — tenta separar
// o número (1º número encontrado, mesma convenção do `_billing_address` no
// backend) pra pré-popular o campo "Número" sem perder dado de cadastros
// antigos, que nunca tiveram esse campo separado.
function splitAddress(raw) {
  const m = (raw || '').match(/\d+/);
  if (!m) return { street: raw || '', number: '' };
  const street = (raw.slice(0, m.index) + raw.slice(m.index + m[0].length))
    .replace(/,\s*$/, '').replace(/^\s*,/, '').trim();
  return { street, number: m[0] };
}

// Editar Perfil — dados pessoais, endereço, alterar senha.
// `user`: PatientOut (GET /patients/me) — phone_masked/cpf_masked nunca vêm
// em texto puro (LGPD), então o campo de celular começa vazio (placeholder
// mostra o valor mascarado) e só é enviado se o paciente digitar um novo.
export default function ProfileEditScreen({ user, onSave, onBack }) {
  const initialAddress = splitAddress(user.address);
  const [form, setForm] = useState({
    firstName: user.first_name || '',
    lastName: user.last_name || '',
    phone: '',
    country: DEFAULT_COUNTRY,
    cep: user.cep || '',
    street: initialAddress.street,
    number: initialAddress.number,
    complement: user.complement || '',
    neighborhood: user.neighborhood || '',
    city: user.city || '',
    state: user.state || '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || null);

  // Endereço "travado" (só Número/Complemento editáveis) quando o CEP foi
  // resolvido com os 4 campos — mesma cascata da busca de laboratório
  // (BrasilAPI → ViaCEP → Google por último, ver `services/cep.py`).
  const [addressLocked, setAddressLocked] = useState(false);
  const [cepLookingUp, setCepLookingUp] = useState(false);
  const [cepNotFound, setCepNotFound] = useState(false);

  const cepDigits = (form.cep || '').replace(/\D/g, '');
  // CEP já vinha salvo com o perfil (dado que pode ter sido preenchido à
  // mão antes desse autofill existir) — evita re-buscar/travar os campos
  // só por abrir a tela; a busca só entra quando o paciente de fato mexe
  // no CEP.
  const initialCepDigits = useRef((user.cep || '').replace(/\D/g, ''));
  const skippedInitialLookup = useRef(false);

  useEffect(() => {
    if (cepDigits.length !== 8) { setAddressLocked(false); setCepNotFound(false); return; }
    if (!skippedInitialLookup.current && cepDigits === initialCepDigits.current) {
      skippedInitialLookup.current = true;
      return;
    }
    skippedInitialLookup.current = true;
    let alive = true;
    setCepLookingUp(true);
    setCepNotFound(false);
    lookupCep(cepDigits)
      .then((data) => {
        if (!alive) return;
        setForm((f) => ({
          ...f,
          street: data.street || f.street,
          neighborhood: data.neighborhood || f.neighborhood,
          city: data.city || f.city,
          state: data.state || f.state,
        }));
        setAddressLocked(!!data.complete);
        setCepNotFound(!data.complete);
      })
      .catch(() => { if (alive) { setAddressLocked(false); setCepNotFound(true); } })
      .finally(() => { if (alive) setCepLookingUp(false); });
    return () => { alive = false; };
  }, [cepDigits]);

  const [changingPwd, setChangingPwd] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const toggleShowPwd = (k) => setShowPwd((s) => ({ ...s, [k]: !s[k] }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pwdRules = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(pwd.next) }));
  const pwdValid = !changingPwd || (
    pwd.current.length > 0 && pwdRules.every((r) => r.ok) &&
    pwd.confirm.length > 0 && pwd.next === pwd.confirm
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!pwdValid || saving) return;
    setError('');
    setSaving(true);
    try {
      if (avatarFile) {
        setAvatarUploading(true);
        const withAvatar = await uploadAvatar(avatarFile);
        setAvatarUrl(withAvatar.avatar_url);
        setAvatarUploading(false);
        setAvatarFile(null);
      }

      const updated = await updateMe({
        firstName: form.firstName,
        lastName: form.lastName,
        ...(form.phone ? { phone: getCountry(form.country).dial + form.phone.replace(/\D/g, '') } : {}),
        cep: form.cep,
        address: [form.street, form.number].filter(Boolean).join(', '),
        complement: form.complement,
        neighborhood: form.neighborhood,
        city: form.city,
        state: form.state,
      });

      if (changingPwd) {
        await changePassword({
          currentPassword: pwd.current,
          newPassword: pwd.next,
          confirmPassword: pwd.confirm,
        });
      }

      onSave?.(updated);
      setForm((f) => ({ ...f, phone: '', country: DEFAULT_COUNTRY }));
      setChangingPwd(false);
      setPwd({ current: '', next: '', confirm: '' });
      setShowPwd({ current: false, next: false, confirm: false });
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível salvar. Tente novamente.');
    } finally {
      setAvatarUploading(false);
      setSaving(false);
    }
  };

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <MobHeader title="Editar perfil" onBack={onBack}/>
      <div style={{ flex: 1, padding: '20px 22px 28px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <AvatarDropzone value={avatarUrl} uploading={avatarUploading} onFileSelected={setAvatarFile}/>
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && (
            <div role="alert" style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--danger-tint)', color: 'var(--danger-strong)', fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Icon.info size={16} color="var(--danger-strong)"/>{error}
            </div>
          )}

          <div>
            <label className="input-label">Nome completo</label>
            <input className="input" value={form.firstName} onChange={set('firstName')} placeholder="Nome"/>
          </div>
          <div>
            <label className="input-label">Sobrenome</label>
            <input className="input" value={form.lastName} onChange={set('lastName')} placeholder="Sobrenome"/>
          </div>
          <div>
            <label className="input-label">E-mail</label>
            <input className="input" type="email" value={user.email} disabled style={{ opacity: 0.65 }}/>
          </div>
          <div>
            <label className="input-label">Celular</label>
            <CountryPhoneInput
              country={form.country}
              onCountryChange={(country) => setForm((f) => ({ ...f, country }))}
              value={form.phone}
              onChange={(phone) => setForm((f) => ({ ...f, phone }))}
              placeholder={user.phone_masked || undefined}
            />
          </div>

          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }}/>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-display)' }}>
            Endereço
          </h3>

          <div>
            <label className="input-label">CEP</label>
            <input className="input" value={form.cep} onChange={(e) => setForm((f) => ({ ...f, cep: maskCep(e.target.value) }))} inputMode="numeric"/>
            {cepLookingUp && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Buscando endereço…</div>}
            {!cepLookingUp && cepNotFound && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                CEP não encontrado automaticamente — preencha o endereço abaixo.
              </div>
            )}
          </div>
          <div>
            <label className="input-label">Endereço</label>
            <input
              className="input"
              value={form.street}
              onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
              disabled={addressLocked}
              style={addressLocked ? { background: 'var(--marble-soft)' } : undefined}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="input-label">Número</label>
              <input className="input" value={form.number} onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))} inputMode="numeric"/>
            </div>
            <div>
              <label className="input-label">Complemento</label>
              <input className="input" value={form.complement} onChange={set('complement')}/>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="input-label">Bairro</label>
              <input
                className="input"
                value={form.neighborhood}
                onChange={(e) => setForm((f) => ({ ...f, neighborhood: e.target.value }))}
                disabled={addressLocked}
                style={addressLocked ? { background: 'var(--marble-soft)' } : undefined}
              />
            </div>
            <div>
              <label className="input-label">Estado</label>
              <input
                className="input"
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value.toUpperCase() }))}
                maxLength={2}
                style={{ textTransform: 'uppercase', ...(addressLocked ? { background: 'var(--marble-soft)' } : {}) }}
                disabled={addressLocked}
              />
            </div>
          </div>
          <div>
            <label className="input-label">Cidade</label>
            <input
              className="input"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              disabled={addressLocked}
              style={addressLocked ? { background: 'var(--marble-soft)' } : undefined}
            />
          </div>

          <div style={{ height: 1, background: 'var(--border)', margin: '4px 0' }}/>

          {!changingPwd ? (
            <button type="button" onClick={() => setChangingPwd(true)} className="btn btn-outline">
              <Icon.lock size={18}/> Alterar senha
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 14, borderRadius: 12, background: 'var(--marble-soft)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)' }}>Alterar senha</h3>
                <button type="button" onClick={() => { setChangingPwd(false); setPwd({ current: '', next: '', confirm: '' }); setShowPwd({ current: false, next: false, confirm: false }); }} style={{ color: 'var(--text-muted)' }}>
                  <Icon.x size={16}/>
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  className="input" type={showPwd.current ? 'text' : 'password'} placeholder="Senha atual"
                  autoComplete="current-password" value={pwd.current}
                  onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                  style={{ paddingRight: 48 }}
                />
                <button
                  type="button" aria-label={showPwd.current ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => toggleShowPwd('current')}
                  style={{ position: 'absolute', right: 10, top: 13, padding: 6, color: 'var(--text-muted)' }}
                >
                  {showPwd.current ? <Icon.eyeOff size={20}/> : <Icon.eye size={20}/>}
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  className="input" type={showPwd.next ? 'text' : 'password'} placeholder="Nova senha"
                  autoComplete="new-password" value={pwd.next}
                  onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                  style={{ paddingRight: 48 }}
                />
                <button
                  type="button" aria-label={showPwd.next ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => toggleShowPwd('next')}
                  style={{ position: 'absolute', right: 10, top: 13, padding: 6, color: 'var(--text-muted)' }}
                >
                  {showPwd.next ? <Icon.eyeOff size={20}/> : <Icon.eye size={20}/>}
                </button>
              </div>
              <PasswordRules rules={pwdRules} active={pwd.next.length > 0}/>
              <div style={{ position: 'relative' }}>
                <input
                  className="input" type={showPwd.confirm ? 'text' : 'password'} placeholder="Confirmar nova senha"
                  autoComplete="new-password" value={pwd.confirm}
                  onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                  style={{ paddingRight: 48 }}
                />
                <button
                  type="button" aria-label={showPwd.confirm ? 'Ocultar senha' : 'Mostrar senha'}
                  onClick={() => toggleShowPwd('confirm')}
                  style={{ position: 'absolute', right: 10, top: 13, padding: 6, color: 'var(--text-muted)' }}
                >
                  {showPwd.confirm ? <Icon.eyeOff size={20}/> : <Icon.eye size={20}/>}
                </button>
              </div>
              {pwd.confirm && pwd.confirm !== pwd.next && (
                <div style={{ fontSize: 12, color: 'var(--danger)' }}>As senhas não conferem.</div>
              )}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={!pwdValid || saving} style={{ marginTop: 8 }}>
            {saved ? <><Icon.check size={18} color="var(--white)"/> Salvo!</> : saving ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </form>
      </div>
    </div>
  );
}
